import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { getBuyers, createBuyer, deleteBuyer, updateBuyer, getCustomers, searchCustomers, getCustomerBulkSelectionMeta, createCustomer, deleteCustomer, getCustomerDetail, updateCustomer } from "../api";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../contexts/NotificationContext";

export const useGetCustomers = (page = 1, pageSize = 10, enabled = true, { accountFilter } = {}) => {
  return useQuery({
    queryKey: ["customers", page, pageSize, accountFilter || "all"],
    queryFn: () => getCustomers(page, pageSize, { accountFilter }),
    enabled,
    keepPreviousData: true,
  });
};

export const useSearchCustomers = ({ name = '', ssn = '', accountFilter } = {}, enabled = false) => {
  const hasInput = name.trim().length >= 2 || ssn.trim().length >= 2;
  return useQuery({
    queryKey: ["customers-search", name, ssn, accountFilter || "all"],
    queryFn: () => searchCustomers({ name, ssn, accountFilter }),
    enabled: enabled && hasInput,
    keepPreviousData: true,
    staleTime: 1000 * 30,
  });
};

export const useGetCustomerBulkSelectionMeta = ({ accountFilter, search, contact }, enabled = false) => {
  return useQuery({
    queryKey: ["customers-bulk-selection-meta", accountFilter || "all", search || "", contact],
    queryFn: () => getCustomerBulkSelectionMeta({ accountFilter, search, contact }),
    enabled,
    staleTime: 1000 * 30,
  });
};

export const useGetCustomerDetail = (id, tab) => {
  const { data, isLoading, status, isError, error, isSuccess, isFetching } = useQuery({
    queryKey: ["customer-detail", id, tab],
    queryFn: () => getCustomerDetail(id, tab),
    enabled: !!id && !!tab,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
    isFetching,
  };
};

export const useUpdateCustomer = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: ({ id, data }) => updateCustomer(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["customers"] });
      await queryClient.invalidateQueries({ queryKey: ["customer-detail"] });
      await queryClient.invalidateQueries({ queryKey: ["calculus-detail"] });
      await queryClient.invalidateQueries({ queryKey: ["account"] });

      // Show success notification
      showNotification('success', 'Customer updated successfully!');

      // Navigate immediately without setTimeout
    },
    onError: (error) => {
      console.error("Error updating customer:", error);

      // Show error notification
      let errorMessage = 'Error updating customer. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });

  return mutation;
};
export const useCreateCustomer = ({ onClose } = {}) => {
  // onClose callback'ini memoize et
  const memoizedOnClose = useCallback((customerId) => {
    if (onClose) {
      onClose(customerId);
    }
  }, [onClose]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (data) => createCustomer(data),
    onSuccess: async (_variables, data) => {

      await queryClient.invalidateQueries({ queryKey: ["customers"] });
      await queryClient.invalidateQueries({ queryKey: ["account"] });

      // Invalidate the specific deal query
      await queryClient.invalidateQueries({ queryKey: ["deal", _variables.deal_id, _variables.tab] });

      // Also invalidate all deal queries to ensure all tabs are updated
      await queryClient.invalidateQueries({ queryKey: ["deal"] });

      // Force refetch the specific deal query
      await queryClient.refetchQueries({ queryKey: ["deal", _variables.deal_id, _variables.tab] });

      // Show success notification
      showNotification('success', 'Customer created successfully!');

      // Navigate immediately without setTimeout
      if (onClose) {
        memoizedOnClose(_variables?.customer_id);
      } else {
        navigate(_variables?.customer_id ? `/customers/${_variables.customer_id}` : '/customers');
      }
    },
    onError: (error) => {
      console.error("Error creating customer:", error);

      // Show error notification
      let errorMessage = 'Error creating customer. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};
export const useDeleteCustomer = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (id) => deleteCustomer(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["customers"] });
      await queryClient.invalidateQueries({ queryKey: ["deal"] });
      await queryClient.invalidateQueries({ queryKey: ["account"] });

      // Show success notification
      showNotification('success', 'Customer deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting customer:", error);

      // Show error notification
      let errorMessage = 'Error deleting customer. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};


export const useCreateBuyer = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (data) => createBuyer(data),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["buyers"] });

      // Show success notification
      showNotification('success', 'Buyer created successfully!');

      // Navigate immediately without setTimeout
      navigate('/inventory-parameters?tab=buyers');
    },
    onError: (error) => {
      console.error("Error creating buyer:", error);

      // Show error notification
      let errorMessage = 'Error creating buyer. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useUpdateBuyer = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: ({ id, data }) => updateBuyer(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["buyers"] });

      // Show success notification
      showNotification('success', 'Buyer updated successfully!');

      // Navigate immediately without setTimeout
      navigate('/inventory-parameters?tab=buyers');
    },
    onError: (error) => {
      console.error("Error updating buyer:", error);

      // Show error notification
      let errorMessage = 'Error updating buyer. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });

  return mutation;
};

export const useDeleteBuyer = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (id) => deleteBuyer(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["buyers"] });

      // Show success notification
      showNotification('success', 'Buyer deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting buyer:", error);

      // Show error notification
      let errorMessage = 'Error deleting buyer. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};